// Hai cột trang chủ: trái = thông báo (công ty / của tôi) + việc hôm nay, phải = cá nhân + chỉ tiêu tháng + lịch hôm nay
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Dropdown, InputNumber, Modal, Select, Tabs, message } from 'antd';
import dayjs from 'dayjs';
import useAuthStore from 'store/authStore';
import { DashboardUser, UserAvatar } from '../component/user';
import { HOME_ROLES, ROLE_META, detectRole, moneyShort, type HomeRole } from './homeData';
import useHomeStore, { DEMO_SEED_VER } from './store';
import { AnnouncementPanel } from './Announcements';
import { HOME_DEMO, useHomeData, type HomeData, type HomeKpi } from './useHomeData';
import { Card, CardHead, DemoChip, Ring, TONE_TEXT, TONE_TILE } from './ui';

export interface HomeCtx {
  user: DashboardUser;
  /** Nhóm thật của người dùng */
  ownRole: HomeRole;
  /** Nhóm đang hiển thị (super admin có thể xem như nhóm khác) */
  role: HomeRole;
  isSuperAdmin: boolean;
  canManage: boolean;
  data: HomeData;
  settingsOpen: boolean;
  setSettingsOpen: (v: boolean) => void;
}

export function useHomeCtx(user: DashboardUser): HomeCtx {
  const { isSuperAdmin } = useAuthStore();
  const roleMap = useHomeStore(s => s.roleMap);
  const viewAs = useHomeStore(s => s.viewAs);
  const targets = useHomeStore(s => s.targets);
  const personal = useHomeStore(s => s.personalTargets[user.key]);
  const seedVer = useHomeStore(s => s.seedVer);
  const seedDemo = useHomeStore(s => s.seedDemo);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const ownRole = detectRole(user.roleName, isSuperAdmin, roleMap);
  const role = isSuperAdmin && viewAs ? viewAs : ownRole;
  const target = personal && role === ownRole ? { ...targets[role], value: personal } : targets[role];
  const data = useHomeData(role, user, target);

  // Thông báo mẫu cho môi trường dev (một lần)
  useEffect(() => { if (HOME_DEMO && (seedVer || 0) < DEMO_SEED_VER) seedDemo('Ban Giám đốc'); }, [seedVer, seedDemo]);

  return { user, ownRole, role, isSuperAdmin, canManage: isSuperAdmin || ownRole === 'manager', data, settingsOpen, setSettingsOpen };
}

// ---------------------------------------------------------------------------
// Cột trái

export function LeftPanel({ ctx }: { ctx: HomeCtx }) {
  const { role, user } = ctx;
  return (
    <div className="space-y-3">
      <AnnouncementPanel role={role} me={{ key: user.key, name: user.fullname || user.email }} recipientId={user.userId} canManage={ctx.canManage} />
      <TodayCard data={ctx.data} />
    </div>
  );
}

function TodayCard({ data }: { data: HomeData }) {
  return (
    <Card>
      <CardHead title="Việc hôm nay" count={data.todos.length} demo={data.statsDemo} />
      {data.loading && !data.stats.length ? (
        <div className="px-4 pb-4 text-xs text-slate-500"><i className="fa-solid fa-spinner fa-spin mr-1.5" />Đang tải…</div>
      ) : (
        <>
          {data.stats.length > 0 && (
            <div className="grid grid-cols-3 gap-2 px-3">
              {data.stats.map(s => {
                const body = (
                  <>
                    <div className="text-lg font-semibold leading-tight tabular-nums text-white">{s.value}</div>
                    <div className="truncate text-[11px] text-slate-400">{s.label}</div>
                  </>
                );
                return s.to
                  ? <Link key={s.label} to={s.to} className="rounded-xl bg-white/[0.04] px-2.5 py-2 transition hover:bg-white/[0.08]">{body}</Link>
                  : <div key={s.label} className="rounded-xl bg-white/[0.04] px-2.5 py-2">{body}</div>;
              })}
            </div>
          )}

          <div className="px-2 pb-2 pt-2">
            {data.todos.length ? data.todos.map(t => (
              <Link key={t.key} to={t.to} className="group flex items-center gap-3 rounded-xl px-2 py-2 transition hover:bg-white/[0.05]">
                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${TONE_TILE[t.tone]}`}><i className={`${t.icon} text-[13px]`} /></span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] text-slate-100">{t.label}</span>
                  {t.extra && <span className="block text-[11px] text-slate-500">{t.extra}</span>}
                </span>
                <span className={`text-[15px] font-semibold tabular-nums ${TONE_TEXT[t.tone]}`}>{t.count}</span>
                <i className="fa-solid fa-chevron-right text-[10px] text-slate-600 transition group-hover:translate-x-0.5 group-hover:text-slate-300" />
              </Link>
            )) : (
              <div className="flex items-center gap-3 rounded-xl px-2 py-2.5">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-300"><i className="fa-solid fa-check" /></span>
                <span className="text-[13px] text-slate-300">Không còn việc tồn đọng</span>
              </div>
            )}
          </div>

        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Cột phải

const fmtVal = (k: Pick<HomeKpi, 'unit'>, v: number) => (k.unit === 'đ' ? moneyShort(v) : `${v} ${k.unit}`);

export function RightPanel({ ctx }: { ctx: HomeCtx }) {
  return (
    <div className="space-y-3">
      <ProfileCard ctx={ctx} />
      {ctx.data.schedule && <ScheduleCard schedule={ctx.data.schedule} />}
      {ctx.isSuperAdmin && <HomeSettingsModal open={ctx.settingsOpen} onClose={() => ctx.setSettingsOpen(false)} user={ctx.user} />}
    </div>
  );
}

/** Lịch trong ngày: ưu tiên lịch sắp tới, lịch đã qua làm mờ */
function ScheduleCard({ schedule }: { schedule: NonNullable<HomeData['schedule']> }) {
  const now = dayjs();
  const rows = schedule.rows;
  const upcoming = rows.filter(r => dayjs(r.at).isAfter(now.subtract(30, 'minute')));
  const shownRows = (upcoming.length ? upcoming : rows.slice(-4)).slice(0, 4);
  const nextAt = upcoming[0]?.at;

  return (
    <Card>
      <CardHead title={schedule.title} demo={schedule.demo} extra={rows.length > shownRows.length && (
        <Link to={shownRows[0]?.to || '/appointments'} className="rounded-lg px-2 py-1 text-xs font-medium text-slate-400 hover:bg-white/10 hover:text-white">Tất cả {rows.length}</Link>
      )} />
      <div className="px-3 pb-3">
        {!shownRows.length ? <div className="px-1 py-1 text-xs text-slate-500">Hôm nay chưa có lịch</div> : shownRows.map((r, i) => {
          const past = dayjs(r.at).isBefore(now.subtract(30, 'minute'));
          const next = r.at === nextAt;
          return (
            <Link key={i} to={r.to} className={`flex items-center gap-3 rounded-lg px-1 py-1 transition hover:bg-white/[0.05] ${past ? 'opacity-50' : ''}`}>
              <span className={`w-12 shrink-0 rounded-md py-0.5 text-center text-xs font-semibold tabular-nums ${next ? 'bg-teal-400/20 text-teal-200' : 'text-slate-300'}`}>
                {dayjs(r.at).format('HH:mm')}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] text-slate-100">{r.title}</span>
                {r.sub && <span className="block truncate text-[11px] text-slate-500">{r.sub}</span>}
              </span>
            </Link>
          );
        })}
      </div>
    </Card>
  );
}

function ProfileCard({ ctx }: { ctx: HomeCtx }) {
  const { user, role, data } = ctx;
  const setViewAs = useHomeStore(s => s.setViewAs);
  const meta = ROLE_META[role];
  const k = data.kpi;
  const daysLeft = dayjs().daysInMonth() - dayjs().date() + 1;
  const remain = k ? Math.max(0, k.target - k.value) : 0;
  const perDay = remain / daysLeft;

  return (
    <Card>
      <div className="flex items-start gap-3 px-4 pt-4">
        <div className="rounded-full p-[2px]" style={{ background: meta.color }}><div className="rounded-full bg-slate-900 p-[2px]"><UserAvatar user={user} size="lg" /></div></div>
        <div className="min-w-0 flex-1">
          <Link to="/admin/profile" className="block truncate text-[15px] font-semibold text-white hover:underline">{user.fullname || user.email}</Link>
          {ctx.isSuperAdmin ? (
            <Dropdown trigger={['click']} menu={{
              selectedKeys: [role],
              items: [{ type: 'group', label: 'Xem trang chủ như', children: HOME_ROLES.map(r => ({ key: r, label: ROLE_META[r].label })) }],
              onClick: ({ key }) => setViewAs(key === ctx.ownRole ? null : key as HomeRole),
            }}>
              <button className="mt-0.5 inline-flex items-center gap-1.5 rounded-md text-xs font-medium hover:brightness-125" style={{ color: meta.color }} title="Xem trang chủ của nhóm khác">
                <i className={meta.icon} />{meta.label}<i className="fa-solid fa-chevron-down text-[9px] opacity-70" />
              </button>
            </Dropdown>
          ) : (
            <div className="mt-0.5 text-xs font-medium" style={{ color: meta.color }}><i className={`${meta.icon} mr-1.5`} />{meta.label}</div>
          )}
          <div className="mt-0.5 flex items-center gap-1.5 truncate text-[11px] text-slate-400" title={HOME_DEMO ? 'Số mẫu – chờ API chấm công, nhân sự' : user.email}>
            {HOME_DEMO
              ? <><i className="fa-regular fa-clock text-emerald-300" />Vào ca <b className="font-semibold text-slate-200">07:52</b> · Phép <b className="font-semibold text-slate-200">5,5</b> <DemoChip /></>
              : <span className="truncate">{[user.roleName, user.branchName].filter(Boolean).join(' · ') || user.email}</span>}
          </div>
        </div>
      </div>

      <div className="mt-3 border-t border-white/[0.06] px-4 pb-3.5 pt-3">
        <div className="mb-2 flex items-center gap-2">
          <span className="text-[13px] font-semibold text-white">Chỉ tiêu tháng {dayjs().format('M')}</span>
          {(k?.demo || data.metrics.some(m => m.demo)) && <DemoChip />}
          <span className="flex-1" />
          {ctx.isSuperAdmin && (
            <button onClick={() => ctx.setSettingsOpen(true)} className="flex h-6 w-6 items-center justify-center rounded-md text-slate-500 hover:bg-white/10 hover:text-white" title="Cài đặt chỉ tiêu và nhóm">
              <i className="fa-solid fa-gear text-xs" />
            </button>
          )}
        </div>
        {data.loading && !k ? <div className="py-4 text-center text-xs text-slate-500"><i className="fa-solid fa-spinner fa-spin mr-1.5" />Đang tải…</div> : !k ? (
          <div className="py-3 text-xs text-slate-500">Chưa có số liệu tháng này</div>
        ) : (
          <>
            <div className="flex items-center gap-4">
              <Ring pct={k.target ? (k.value / k.target) * 100 : 0} size={84} />
              <div className="min-w-0 flex-1">
                <div className="text-[11px] text-slate-400">{k.label}</div>
                <div className="text-[22px] font-semibold leading-tight tabular-nums text-white">{fmtVal(k, k.value)}</div>
                <div className="text-[11px] text-slate-500">trên {fmtVal(k, k.target)}</div>
                <div className="mt-1.5 text-[11px] leading-snug">
                  {remain > 0
                    ? <span className="text-slate-300">Còn {fmtVal(k, remain)} · {daysLeft} ngày<br /><span className="text-teal-300">≈ {fmtVal(k, k.unit === 'đ' ? perDay : Math.ceil(perDay))}/ngày</span></span>
                    : <span className="text-emerald-300"><i className="fa-solid fa-circle-check mr-1" />Đã đạt chỉ tiêu</span>}
                </div>
              </div>
            </div>
            {data.metrics.map(m => (
              <div key={m.label} className="mt-3">
                <div className="flex items-baseline justify-between gap-2 text-xs">
                  <span className="text-slate-400">{m.label}</span>
                  <span className="font-medium tabular-nums text-slate-100">{m.text}</span>
                </div>
                {m.pct !== null && (
                  <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/[0.08]">
                    <div className="h-full rounded-full bg-sky-400" style={{ width: `${Math.min(100, m.pct)}%` }} />
                  </div>
                )}
              </div>
            ))}
            {data.note && (
              <div className="mt-3 flex items-center gap-2 rounded-lg bg-amber-400/10 px-2.5 py-1.5 text-xs text-amber-200">
                <i className="fa-solid fa-trophy" /><span className="truncate">{data.note}</span>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------

function HomeSettingsModal({ open, onClose, user }: { open: boolean; onClose: () => void; user: DashboardUser }) {
  const s = useHomeStore();
  const [roleName, setRoleName] = useState('');
  const roleNames = Object.keys(s.roleMap);
  const demoCount = s.announcements.filter(a => a.demo).length;

  return (
    <Modal open={open} onCancel={onClose} footer={<Button onClick={onClose}>Đóng</Button>} title="Cài đặt trang chủ" width={620}>
      <Tabs items={[
        {
          key: 'targets', label: 'Chỉ tiêu tháng', children: (
            <div className="space-y-3">
              {HOME_ROLES.map(r => (
                <div key={r} className="flex items-center gap-3">
                  <span className="w-36 text-sm font-medium">{ROLE_META[r].label}</span>
                  <span className="w-36 text-xs text-slate-500">{s.targets[r].label}</span>
                  <InputNumber className="!w-48" min={0} value={s.targets[r].value} addonAfter={s.targets[r].unit}
                    formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')} parser={v => Number((v || '').replace(/\./g, '')) as any}
                    onChange={v => v !== null && s.setTarget(r, Number(v))} />
                </div>
              ))}
              <div className="flex items-center gap-3 border-t pt-3">
                <span className="w-36 text-sm font-medium">Chỉ tiêu riêng của tôi</span>
                <InputNumber className="!w-48" min={0} placeholder="Theo nhóm" value={s.personalTargets[user.key] ?? null}
                  onChange={v => s.setPersonalTarget(user.key, v ? Number(v) : null)} />
              </div>
              <div className="text-xs text-slate-400">Khi có API mục tiêu từng nhân viên, số này sẽ lấy từ hệ thống.</div>
            </div>
          ),
        },
        {
          key: 'roles', label: 'Nhóm theo vai trò', children: (
            <div className="space-y-3">
              <div className="text-xs text-slate-500">
                Hệ thống tự đoán nhóm theo tên vai trò (VD "Telesale", "Bác sĩ", "Sale cơ sở", "Quản lý"). Gán tay ở đây nếu tên vai trò không rõ.
              </div>
              <div className="flex gap-2">
                <input value={roleName} onChange={e => setRoleName(e.target.value)} placeholder="Tên vai trò đúng như trong Phân quyền"
                  className="h-8 flex-1 rounded-md border border-slate-300 px-2 text-sm outline-none focus:border-[#3e91ab]" />
                <Button disabled={!roleName.trim()} onClick={() => { s.setRoleMap(roleName.trim(), detectRole(roleName.trim(), false)); setRoleName(''); }}>Thêm</Button>
              </div>
              {roleNames.map(n => (
                <div key={n} className="flex items-center gap-2">
                  <span className="flex-1 truncate text-sm">{n}</span>
                  <Select size="small" className="w-40" value={s.roleMap[n]} onChange={v => s.setRoleMap(n, v)}
                    options={HOME_ROLES.map(r => ({ value: r, label: ROLE_META[r].label }))} />
                  <Button size="small" type="text" danger onClick={() => s.setRoleMap(n, null)}>Bỏ</Button>
                </div>
              ))}
            </div>
          ),
        },
        ...(HOME_DEMO ? [{
          key: 'demo', label: 'Dữ liệu mẫu', children: (
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm">{demoCount} thông báo mẫu</span>
              {demoCount
                ? <Button danger onClick={() => { s.clearDemo(); message.success('Đã xóa thông báo mẫu'); }}>Xóa thông báo mẫu</Button>
                : <Button onClick={() => { s.seedDemo('Ban Giám đốc'); message.success('Đã tạo thông báo mẫu'); }}>Tạo thông báo mẫu</Button>}
            </div>
          ),
        }] : []),
      ]} />
    </Modal>
  );
}
